import type { FundingRow, Rating, ScaleQuestion } from "./types";

export const TRACK_ORDER = [
  "Pflicht",
  "Verstetigung",
  "Regelzuwendung",
  "Eigene Position",
  "Projekttopf",
  "Einzelperson",
  "Reserve",
  "Anderer Topf",
  "Ausschluss",
];

export const eur = (v: number) =>
  v.toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
export const num = (v: number) =>
  v.toLocaleString("de-DE", { maximumFractionDigits: 2 });

/** Parses "1.234,56", "12.500" (as num() writes it), "1234.5" or "1234"; null when invalid. */
export const parseAmount = (s: string): number | null => {
  const t = s.replace(/[€\s]/g, "");
  if (!t) return null;
  const grouped = /^\d{1,3}(\.\d{3})+$/.test(t);
  const n =
    t.includes(",") || grouped
      ? Number(t.replace(/\./g, "").replace(",", "."))
      : Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
};

/** Sections of Doc 18: base without rank, priorities by rank, the rest below the line. */
const SECTIONS = ["Sockel", "Prioritäten", "Rest"] as const;
const sectionOf = (z: FundingRow) => z.bewertung?.teil ?? "Rest";

const indexIn = <T>(list: readonly T[], v: T) => {
  const i = list.indexOf(v);
  return i < 0 ? list.length : i;
};

const byNumber = (a: FundingRow, b: FundingRow) =>
  (a.nr ?? "~").localeCompare(b.nr ?? "~", "de", { numeric: true });

/** Volt ranking, the order of Doc 18: section, then rank, then track, then number. */
export const sortRows = (rows: FundingRow[]): FundingRow[] =>
  [...rows].sort(
    (a, b) =>
      indexIn(SECTIONS, sectionOf(a)) - indexIn(SECTIONS, sectionOf(b)) ||
      (a.bewertung?.rang ?? Infinity) - (b.bewertung?.rang ?? Infinity) ||
      indexIn(TRACK_ORDER, a.bewertung?.spur ?? "") -
        indexIn(TRACK_ORDER, b.bewertung?.spur ?? "") ||
      byNumber(a, b),
  );

export type FundingSort = "number" | "ranking";
export const FUNDING_SORTS: {
  value: FundingSort;
  label: string;
  standard?: boolean;
}[] = [
  { value: "number", label: "Nummer", standard: true },
  { value: "ranking", label: "Volt-Ranking" },
];

/** A year has a Volt ranking when at least one row carries a rating. */
export const hasRanking = (rows: FundingRow[]) =>
  rows.some((r) => r.bewertung !== null);

export const sortedRows = (
  rows: FundingRow[],
  by: FundingSort,
  dir: "asc" | "desc",
): FundingRow[] => {
  const asc =
    by === "ranking" && hasRanking(rows)
      ? sortRows(rows)
      : [...rows].sort(byNumber);
  return dir === "desc" ? asc.reverse() : asc;
};

/** District from the rating, else the place the application names. */
export const placeOf = (z: FundingRow) => z.bewertung?.stadtteil ?? z.ort ?? "";

/** Amount proposed when a row goes on the list: the standard amount, else the request. */
export const proposedAmount = (z: FundingRow) =>
  (z.bewertung?.richtwert ?? 0) > 0 ? z.bewertung!.richtwert! : z.beantragt;

export const pointsReason = (f: ScaleQuestion, b: Rating): string => {
  const p = b[f.schluessel];
  if (p === null) return "nicht bewertet";
  if (
    f.schluessel === "k1" &&
    b.einzugsgebiet === "Bezirk" &&
    f.grundBezirksweit
  )
    return f.grundBezirksweit;
  if (f.schluessel === "k1" && b.einzugsgebiet && b.k1_anteil != null)
    return `Einzugsgebiet ${b.einzugsgebiet}: ${b.k1_anteil.toLocaleString("de-DE")} % leben in Gebieten mit niedrigem oder sehr niedrigem Status.`;
  const text = f.gruende[String(p)] ?? "";
  if (f.jahreVorsatz && b.jahre !== null)
    return f.jahreVorsatz.replace("{n}", String(b.jahre)) + text;
  return text;
};
